import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { usePermissions } from "@/hooks/use-permissions";
import {
  PermissionLock,
  PermissionLockLoading,
} from "@/components/admin/PermissionLock";
import { listSubscribers, broadcastToSubscribers } from "@/lib/admin/subscribers.functions";
import { useLang } from "@/hooks/use-language";
import { Megaphone, Users, Download } from "lucide-react";
import { BackButton } from "@/components/BackButton";

export const Route = createFileRoute("/_authenticated/app/admin/subscribers")({
  component: AdminSubscribers,
  head: () => ({ meta: [{ title: "Subscribers — Admin" }] }),
});

function AdminSubscribers() {
  const perms = usePermissions();
  const { lang, dir } = useLang();
  const isAr = lang === "ar";
  const [tierFilter, setTierFilter] = useState<"all" | "free" | "family" | "premium">("all");
  const [search, setSearch] = useState("");
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [broadcastBody, setBroadcastBody] = useState("");
  const [broadcastAudience, setBroadcastAudience] = useState<
    "all" | "free" | "paid" | "family" | "premium"
  >("all");
  const [notifyEmail, setNotifyEmail] = useState(true);

  const listFn = useServerFn(listSubscribers);
  const broadcastFn = useServerFn(broadcastToSubscribers);

  const allowed = perms.isSuperAdmin;
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["admin-subscribers", tierFilter],
    queryFn: () => listFn({ data: { tier: tierFilter, limit: 500 } }),
    enabled: !perms.loading && allowed,
  });

  const broadcastMut = useMutation({
    mutationFn: () =>
      broadcastFn({
        data: { body: broadcastBody.trim(), audience: broadcastAudience, notify_email: notifyEmail },
      }),
    onSuccess: (r) => {
      toast.success(
        isAr
          ? `أُرسلت إلى ${r.sent} مشترك (إيميل: ${r.emailed ?? 0})`
          : `Sent to ${r.sent} subscribers (emails: ${r.emailed ?? 0})`,
      );
      setBroadcastBody("");
      setShowBroadcast(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.rows ?? []).filter(
      (r) =>
        !q ||
        r.email?.toLowerCase().includes(q) ||
        r.display_name?.toLowerCase().includes(q) ||
        r.country?.toLowerCase().includes(q),
    );
  }, [data, search]);

  function exportCsv() {
    const rows = [
      ["email", "name", "tier", "status", "country", "locale", "age_groups", "signed_up_at", "subscription_ends_at"],
      ...filtered.map((r) => [
        r.email ?? "",
        r.display_name ?? "",
        r.tier,
        r.status,
        r.country ?? "",
        r.locale ?? "",
        r.age_groups.join("|"),
        r.signed_up_at ?? "",
        r.current_period_end ?? "",
      ]),
    ];
    const csv = rows
      .map((row) => row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (perms.loading) return <PermissionLockLoading />;
  if (!allowed) {
    return (
      <PermissionLock
        titleEn="Super admin only"
        titleAr="المسؤول العام فقط"
        messageEn="Subscriber list and broadcasting are restricted to the super admin."
        messageAr="قائمة المشتركين والبثّ الجماعي متاحة للمسؤول العام فقط."
      />
    );
  }

  return (
    <main dir={dir} className="container mx-auto py-8 px-4 max-w-6xl">
      <div className="mb-3"><BackButton /></div>
      <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
        <div>
          <h1 className={`${isAr ? "font-display-ar" : "font-display-en"} text-2xl font-bold`}>
            {isAr ? "المشتركون" : "Subscribers"}
          </h1>
          <p className="text-sm text-foreground/60 mt-1">
            {isAr
              ? "كل المشتركين الجدد بالاشتراكات (مجاني ومدفوع)."
              : "Everyone who signed up (free and paid)."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={exportCsv}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-2xl bg-card border-2 border-clay/40 hover:border-primary text-foreground/70 hover:text-primary font-bold text-xs"
          >
            <Download className="size-3.5" />
            CSV
          </button>
          <button
            onClick={() => setShowBroadcast((v) => !v)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-primary text-primary-foreground font-bold text-sm shadow"
          >
            <Megaphone className="size-4" />
            {isAr ? "رسالة جماعية" : "Broadcast"}
          </button>
        </div>
      </div>

      {showBroadcast && (
        <div className="rounded-3xl border-2 border-primary/40 bg-card p-4 mb-6 space-y-3">
          <div className="flex items-center gap-2 text-sm font-bold">
            <Megaphone className="size-4 text-primary" />
            {isAr ? "إرسال إلى مجموعة من الأهل" : "Send to a group of parents"}
          </div>
          <div className="flex flex-wrap gap-2">
            {(["all", "free", "paid", "family", "premium"] as const).map((a) => (
              <button
                key={a}
                onClick={() => setBroadcastAudience(a)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 ${
                  broadcastAudience === a
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-card border-clay/40"
                }`}
              >
                {isAr
                  ? { all: "الكل", free: "مجاني", paid: "مدفوع", family: "عائلة", premium: "بريميوم" }[a]
                  : a}
              </button>
            ))}
          </div>
          <textarea
            value={broadcastBody}
            onChange={(e) => setBroadcastBody(e.target.value)}
            placeholder={
              isAr
                ? "اكتب رسالتك بهدوء... ستصل لكل الأهل المختارين داخل التطبيق."
                : "Write a calm note... It will reach the selected parents inside the app."
            }
            rows={5}
            maxLength={4000}
            className="w-full rounded-2xl border-2 border-clay/30 focus:border-primary outline-none p-3 text-sm bg-background"
          />
          <label className="flex items-center gap-2 text-xs text-foreground/70">
            <input
              type="checkbox"
              checked={notifyEmail}
              onChange={(e) => setNotifyEmail(e.target.checked)}
            />
            {isAr ? "أرسل أيضاً إيميل تنبيه" : "Also send a notification email"}
          </label>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setShowBroadcast(false)}
              className="px-4 py-2 rounded-2xl bg-card border-2 border-clay/40 font-bold text-xs"
            >
              {isAr ? "إلغاء" : "Cancel"}
            </button>
            <button
              disabled={broadcastMut.isPending || broadcastBody.trim().length === 0}
              onClick={() => {
                if (
                  !confirm(
                    isAr
                      ? "تأكيد إرسال هذه الرسالة لكل المختارين؟"
                      : "Send this message to everyone selected?",
                  )
                )
                  return;
                broadcastMut.mutate();
              }}
              className="px-4 py-2 rounded-2xl bg-primary text-primary-foreground font-bold text-xs disabled:opacity-50"
            >
              {broadcastMut.isPending
                ? isAr
                  ? "جارٍ الإرسال..."
                  : "Sending..."
                : isAr
                  ? "إرسال"
                  : "Send"}
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-3">
        {(["all", "free", "family", "premium"] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTierFilter(t)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 ${
              tierFilter === t
                ? "bg-foreground text-background border-foreground"
                : "bg-card border-clay/40"
            }`}
          >
            {isAr
              ? { all: "الكل", free: "مجاني", family: "عائلة", premium: "بريميوم" }[t]
              : t}
          </button>
        ))}
        <input
          type="text"
          placeholder={isAr ? "بحث بالإيميل / الاسم / البلد" : "Search email / name / country"}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="ms-auto px-3 py-1.5 rounded-full border-2 border-clay/40 bg-card text-xs outline-none focus:border-primary min-w-[200px]"
        />
      </div>

      <div className="flex items-center gap-2 text-xs text-foreground/60 mb-2">
        <Users className="size-3.5" />
        {filtered.length} {isAr ? "مشترك" : "subscribers"}
      </div>

      {isLoading && <p className="text-muted-foreground">{isAr ? "جارٍ التحميل..." : "Loading..."}</p>}
      {error && <p className="text-destructive">{(error as Error).message}</p>}

      <div className="overflow-x-auto rounded-2xl border-2 border-clay/30 bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-left">
            <tr>
              <th className="p-2">{isAr ? "الإيميل" : "Email"}</th>
              <th className="p-2">{isAr ? "الاسم" : "Name"}</th>
              <th className="p-2">{isAr ? "الاشتراك" : "Tier"}</th>
              <th className="p-2">{isAr ? "الحالة" : "Status"}</th>
              <th className="p-2">{isAr ? "البلد" : "Country"}</th>
              <th className="p-2">{isAr ? "الأعمار" : "Ages"}</th>
              <th className="p-2">{isAr ? "تاريخ التسجيل" : "Joined"}</th>
              <th className="p-2">{isAr ? "ينتهي الاشتراك" : "Ends"}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => {
              const endsAt = r.current_period_end ? new Date(r.current_period_end) : null;
              const now = Date.now();
              const daysLeft = endsAt ? Math.ceil((endsAt.getTime() - now) / 86400000) : null;
              const expired = endsAt ? endsAt.getTime() < now : false;
              const soon = daysLeft !== null && daysLeft >= 0 && daysLeft <= 7;
              return (
                <tr key={r.user_id} className="border-t border-clay/20">
                  <td className="p-2 font-mono text-xs">
                    {r.email ? (
                      <a className="text-primary hover:underline" href={`mailto:${r.email}`}>
                        {r.email}
                      </a>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="p-2">{r.display_name ?? "—"}</td>
                  <td className="p-2">
                    <span
                      className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                        r.tier === "premium"
                          ? "bg-violet-500/15 text-violet-700 dark:text-violet-300"
                          : r.tier === "family"
                            ? "bg-amber-500/15 text-amber-700 dark:text-amber-300"
                            : "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                      }`}
                    >
                      {r.tier}
                    </span>
                  </td>
                  <td className="p-2 text-xs">{r.status}</td>
                  <td className="p-2">{r.country ?? "—"}</td>
                  <td className="p-2 text-xs">{r.age_groups.length > 0 ? r.age_groups.join(", ") : "—"}</td>
                  <td className="p-2 text-xs">
                    {r.signed_up_at ? new Date(r.signed_up_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="p-2 text-xs">
                    {endsAt ? (
                      <span
                        className={`inline-flex flex-col ${
                          expired
                            ? "text-destructive"
                            : soon
                              ? "text-amber-600 dark:text-amber-400"
                              : "text-foreground/70"
                        }`}
                      >
                        <span>{endsAt.toLocaleDateString()}</span>
                        <span className="text-[10px] opacity-70">
                          {expired
                            ? isAr
                              ? "منتهي"
                              : "expired"
                            : isAr
                              ? `بعد ${daysLeft} يوم`
                              : `in ${daysLeft}d`}
                        </span>
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && !isLoading && (
              <tr>
                <td colSpan={8} className="p-6 text-center text-foreground/50">
                  {isAr ? "لا يوجد مشتركون مطابقون" : "No matching subscribers"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4">
        <Link to="/app/admin" className="text-xs text-primary hover:underline">
          ← {isAr ? "العودة لإدارة المحتوى" : "Back to admin"}
        </Link>
      </div>
    </main>
  );
}
